import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, MapPin, Navigation, ExternalLink, Phone } from 'lucide-react';
import LikeButton from '../../../components/LikeButton/LikeButton';
import { Button } from '../../../components/ui/button';

const SearchResultsCard = ({ id, name, website, address, distance, rating, userRatingCount, phone, distanceUnit = 'mi' }) => {
  const { t } = useTranslation();

  const openDirections = () => {
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(mapsUrl, "_blank");
  };
  
  return (
    <div className="bg-white rounded-xl p-3 sm:p-4 border border-border space-y-2.5 sm:space-y-3 shadow-sm">
      {/* Header with Name and Save Button */}
      <div className="flex items-start justify-between gap-1.5 sm:gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground text-sm sm:text-base mb-0.5 sm:mb-1">{name}</h3>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <div className="flex items-center gap-0.5 sm:gap-1">
              <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-xs sm:text-sm font-medium text-foreground">
                {rating || t('no_ratings_available', 'N/A')}
              </span>
            </div>
            {userRatingCount && userRatingCount !== "0" && (
              <span className="text-[10px] sm:text-sm text-muted-foreground">
                ({userRatingCount} {t('reviews', 'reviews')})
              </span>
            )}
            {distance && (
              <span className="text-[10px] sm:text-sm text-muted-foreground">
                · {distanceUnit === 'km' ? `${(distance * 1.60934).toFixed(1)} km` : `${distance.toFixed(1)} mi`}
              </span>
            )}
          </div>
        </div>
        
        {/* Save Button */}
        <div className="flex-shrink-0">
          <LikeButton postId={id} itemType="Place" />
        </div>
      </div>

      {/* Address */}
      <div className="flex items-start gap-1.5 sm:gap-2">
        <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
        <span className="text-xs sm:text-sm text-foreground">{address}</span>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-1.5 sm:gap-2">
        <Button
          className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 sm:gap-2 h-9 sm:h-10 text-xs sm:text-sm"
          onClick={openDirections}
        >
          <Navigation className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          {t('directions', 'Directions')}
        </Button>
        
        {phone && (
          <Button
            variant="outline"
            className="flex-1 gap-1.5 sm:gap-2 h-9 sm:h-10 text-xs sm:text-sm"
            asChild
          >
            <a href={`tel:${phone}`}>
              <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              {t('call', 'Call')}
            </a>
          </Button>
        )}
      </div>

      {website && (
        <Button
          variant="outline"
          className="w-full gap-1.5 sm:gap-2 h-9 sm:h-10 text-xs sm:text-sm border-primary/30 hover:bg-primary/5"
          onClick={() => window.open(website, "_blank")}
        >
          <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          {t('visit_website', 'Visit Website')}
        </Button>
      )}
    </div>
  );
};

export default memo(SearchResultsCard);
